import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Grid,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Modal,
  IconButton,
  Box,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { ThemeProvider } from "@material-ui/core/styles";
import { globalTheme } from "style/globalTheme";
import { DataGrid, GridOverlay } from "@material-ui/data-grid";
import ResponsiveGridContainer from "app/components/atoms/responsiveGridContainer/ResponsiveGridContainer.mui";
import FormWidget from "app/components/mvp/formWidget";
import { getEmployeeProfileRequestSchema } from "./model/employeeProfileRequestSchema";
import { getEmployeeProfileRequestUiSchema } from "./model/employeeProfileRequestUiSchema";
import { CreateEmployeeProfileRequestPayload } from "./model/createEmployeeProfileRequestPayload";
import { config } from "utilities/config";
import { usePreLoader } from "app/services/hooks/usePreLoader";
import clsx from "clsx";

function CustomLoadingOverlay() {
  return (
    <GridOverlay>
      <div style={{ position: "absolute", top: 0, width: "100%" }}>
        <LinearProgress />
      </div>
    </GridOverlay>
  );
}

function CustomNoRowsOverlay() {
  return (
    <GridOverlay>
      <Typography variant="body1" style={{ color: "#999" }}>
        No candidates found
      </Typography>
    </GridOverlay>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: "100%",
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  filterRow: {
    marginBottom: theme.spacing(2),
  },
  filterField: {
    minWidth: 200,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  modalClose: {
    float: "right",
  },
  paper: {
    position: "absolute",
    width: "800px",
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    borderRadius: "3px",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "82vh",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      width: "550px",
    },
    [theme.breakpoints.down("xs")]: {
      width: "400px",
    },
  },
  modalContainer: {
    padding: "3rem 0rem",
    "& div[class^=MuiTabs-flexContainer]": {
      margin: "10px",
    },
    [theme.breakpoints.down("xs")]: {
      margin: "10px",
    },
  },
  modal: {
    "md-dialog": {
      maxHeight: "55% !important",
    },
  },
  /** Ellipsis in cell; full text via Tooltip on hover */
  dataGridLongTextCell: {
    overflow: "hidden !important",
    minWidth: 0,
    "& .MuiDataGrid-cellContent": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  dataGridLongTextHeader: {
    whiteSpace: "normal !important",
    lineHeight: 1.2,
    textAlign: "center",
  },
}));

const renderLongTextCell = (params) => {
  const v = params.value != null ? String(params.value).trim() : "";
  const display = v || "—";
  const showTip = Boolean(v);
  return (
    <Tooltip
      title={
        showTip ? (
          <Typography variant="body2" component="span" style={{ maxWidth: 420, display: "block", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {v}
          </Typography>
        ) : (
          ""
        )
      }
      placement="top-start"
      interactive
      enterDelay={250}
      disableHoverListener={!showTip}
      disableFocusListener={!showTip}
      disableTouchListener={!showTip}
    >
      <Box
        component="span"
        display="block"
        width="100%"
        minWidth={0}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: showTip ? "help" : "default",
        }}
      >
        {display}
      </Box>
    </Tooltip>
  );
};

function EmployeeSearch() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enableLoader } = usePreLoader();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const pageSize = 10;

  // Fixed criteria applied to all connect-employee-search queries (not user-selectable)
  const CONNECT_SEARCH_FIXED_FILTERS = [
    { field: "ctpt_onhold", searchvalue: false, type: "boolean", operator: "eq" },
    { field: "ctpt_connectsearch", searchvalue: true, type: "boolean", operator: "eq" },
    { field: "statecode", searchvalue: 0, type: "int", operator: "eq" },
  ];
  const EMPLOYEE_SEARCH_PROPERTIES = [
    "contactid",
    "firstname",
    "lastname",
    "ctpt_membershipnumber",
    "emailaddress1",
    "ctpt_discipline",
    "ctpt_qualifications",
    "ctpt_professionalregistration",
    "ctpt_provinces",
    "ctpt_branchid",
    "_ctpt_branchid_value",
    "ctpt_employmentstatus",
    "ctpt_willingtorelocate",
    "ctpt_employmenttype",
    "ctpt_applicationtype",
    "statecode",
    "ctpt_onhold",
    "ctpt_connectsearch",
  ];

  // Client-side filter: exclude contacts that fail fixed criteria (safety net if API uses OR for text search)
  const meetsConnectSearchFixedCriteria = (record) => {
    const getFieldValue = (source, fieldName, fallbackFieldName) => {
      if (!source) return undefined;
      const directValue = source?.[fieldName] ?? source?.[fallbackFieldName];
      if (directValue !== undefined) return directValue;

      if (Array.isArray(source?.data)) {
        const field =
          source.data.find((item) => item?.name === fieldName) ||
          source.data.find((item) => item?.name === fallbackFieldName);
        if (field) return field.value;
      }

      return undefined;
    };

    const onHold = getFieldValue(record, "ctpt_onhold", "onhold");
    const connectSearch = getFieldValue(record, "ctpt_connectsearch", "connectsearch");
    const stateCode = getFieldValue(record, "statecode", "statecode");
    const membershipNumber = getFieldValue(record, "ctpt_membershipnumber", "membershipnumber");
    const normalizedOnHold = String(onHold ?? "").toLowerCase().trim();
    const normalizedConnectSearch = String(connectSearch ?? "").toLowerCase().trim();
    const normalizedStateCode = String(stateCode ?? "").toLowerCase().trim();
    const normalizedMembershipNumber = String(membershipNumber ?? "").trim();

    // API responses are sometimes compact and may omit fixed-criteria fields from `data`.
    // Only reject records on explicit failures; treat missing values as already validated by backend filters.
    const isOnHold =
      onHold === true || onHold === 1 || normalizedOnHold === "yes" || normalizedOnHold === "true";
    const isConnectSearchEnabled =
      connectSearch == null
        ? true
        : connectSearch === true ||
          connectSearch === 1 ||
          normalizedConnectSearch === "yes" ||
          normalizedConnectSearch === "true";
    const isActive =
      stateCode == null
        ? true
        : stateCode === 0 || normalizedStateCode === "0" || normalizedStateCode === "active";
    const hasMembershipNumber = membershipNumber == null ? true : normalizedMembershipNumber !== "";
    return !isOnHold && isConnectSearchEnabled && isActive && hasMembershipNumber;
  };

  const normalizeSearchText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const buildSearchVariants = (value) => {
    const rawValue = String(value || "").toLowerCase().trim();
    if (!rawValue) return [];

    const punctuationAsSpace = rawValue.replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
    const compact = punctuationAsSpace.replace(/\s+/g, "");

    return Array.from(new Set([rawValue, punctuationAsSpace, compact].filter(Boolean)));
  };

  const matchesAnySearchVariant = (fieldValue, variants) => {
    if (fieldValue == null) return false;
    const rawFieldValue = String(fieldValue).toLowerCase();
    const normalizedFieldValue = normalizeSearchText(fieldValue);
    const compactFieldValue = normalizedFieldValue.replace(/\s+/g, "");

    return variants.some((variant) => {
      const compactVariant = variant.replace(/\s+/g, "");
      return (
        rawFieldValue.includes(variant) ||
        normalizedFieldValue.includes(variant) ||
        compactFieldValue.includes(compactVariant)
      );
    });
  };

  // New filter fields state - default to "not-selected"
  const NOT_SELECTED = "not-selected";
  const [discipline, setDiscipline] = useState(NOT_SELECTED);
  const [highestQualification, setHighestQualification] = useState(NOT_SELECTED);
  const [professionalRegistration, setProfessionalRegistration] = useState(NOT_SELECTED);
  const [province, setProvince] = useState(NOT_SELECTED);
  const [employmentStatus, setEmploymentStatus] = useState(NOT_SELECTED);
  const [willingToRelocate, setWillingToRelocate] = useState(NOT_SELECTED);
  const [employmentType, setEmploymentType] = useState(NOT_SELECTED);
  const [applicationType, setApplicationType] = useState(NOT_SELECTED);

  // Static values for dropdowns - updated to match requirements
  const disciplineOptions = [
    "Agriculture",
    "Civil",
    "Construction Management",
    "Engineering Management",
    "Environment",
    "Fire",
    "Geology",
    "Geotechnical",
    "Industrial",
    "Information Technology",
    "Marine",
    "Mechanical",
    "Other",
    "Project Management",
    "Quantity Surveying",
    "Railway Infrastructure",
    "Structural",
    "Surveying",
    "Technology and Information Management",
    "Transportation",
    "Urban",
    "Water",
  ];

  const highestQualificationOptions = [
    "B Eng",
    "B Eng (Hons)",
    "BSc Eng (Hons)",
    "BSc Eng",
    "N Dip",
    "GDE",
    "B Eng Tech",
    "M Eng",
    "MSc Eng",
    "B-Tech",
    "PHD",
    "Dip Eng Tech",
    "Other",
  ];

  const professionalRegistrationOptions = [
    "Candidate Construction Manager",
    "Candidate Construction Project Manager",
    "Candidate Engineer",
    "Candidate Engineering Technician",
    "Candidate Engineering Technologist",
    "Candidate Project and Construction Manager",
    "Graduate Member",
    "Professional Construction Manager",
    "Professional Construction Project Manager",
    "Professional Engineer",
    "Professional Engineering Technician",
    "Professional Engineering Technologist",
    "Professional Natural Scientist",
    "Professional Planner",
    "Professional Scientist",
  ];

  // Province/Branch options with GUIDs - hardcoded until API provides dynamic values
  const provinceOptions = [
    { label: "Algoa", guid: "b7b41dce-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Amathole", guid: "2fdfd2c6-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Bloemfontein", guid: "91a989d6-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Drakensberg", guid: "47ac86fc-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Durban", guid: "a2f249bf-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Foreign Member", guid: "28db9884-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Highveld", guid: "e922f316-b52d-ed11-9db1-6045bd88d9f9" },
    { label: "Johannesburg", guid: "58a4059f-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Kimberley", guid: "20fd40e2-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Limpopo", guid: "4a1af291-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Lowveld", guid: "afdd7c07-b52d-ed11-9db1-6045bd88d9f9" },
    { label: "North West", guid: "29d9ed0e-b52d-ed11-9db1-6045bd88d9f9" },
    { label: "Pietermaritzburg", guid: "e27cdfad-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Pretoria", guid: "98723a8b-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Southern Cape", guid: "661aeef5-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Transkei", guid: "28d9ed0e-b52d-ed11-9db1-6045bd88d9f9" },
    { label: "Upington", guid: "4d4e10ef-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Vaal Triangle", guid: "c4cefca6-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Western Cape", guid: "8ca989d6-b42d-ed11-9db1-6045bd88d9f9" },
    { label: "Zululand", guid: "46ac86fc-b42d-ed11-9db1-6045bd88d9f9" },
  ];

  // Employment Status - OptionSet values (aligned with Dataverse choice set)
  const employmentStatusOptions = [
    { label: "Employed", value: 533910000 },
    { label: "Part Time", value: 533910003 },
    { label: "Unemployed", value: 533910001 },
  ];

  // Willing to Relocate - OptionSet values
  const willingToRelocateOptions = [
    { label: "Yes", value: 533910000 },
    { label: "No", value: 533910001 },
  ];

  // Employment Type - OptionSet values
  const employmentTypeOptions = [
    { label: "Full Time", value: 533910000 },
    { label: "Freelance", value: 533910001 },
    { label: "Contract", value: 533910002 },
    { label: "Graduate Program", value: 533910005 },
  ];

  // Application Type - OptionSet values
  const applicationTypeOptions = [
    { label: "Internship", value: 533910000 },
    { label: "Graduate Programme", value: 533910001 },
    { label: "Entry Level", value: 533910002 },
    { label: "Mid Level", value: 533910003 },
    { label: "Senior Management", value: 533910004 },
  ];

  // Helper function to format API response data
  const formatEmployeeData = useCallback((apiData) => {
    // Helper function to get OptionSet label from value
    const getOptionSetLabel = (value, options) => {
      if (value === null || value === undefined || value === "") return "";
      const option = options.find((opt) => opt.value === value);
      return option ? option.label : value;
    };
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((record) => {
      // Handle different response formats
      if (record.data && Array.isArray(record.data)) {
        // Format: { id: "...", data: [{name: "field", value: "value"}, ...] }
        const formatted = { id: record.id || record.contactid };
        record.data.forEach((field) => {
          if (field.name && field.value !== undefined) {
            formatted[field.name] = field.value;
            // For lookup fields (formatted fields), use id if available, otherwise use value
            // This matches the pattern used in OptInToEmployerSearch.js
            if (field.name === '_ctpt_branchid_value' && field.id) {
              formatted[field.name] = field.id; // Use the GUID from the id property
              formatted.ctpt_branchid = field.id; // Also set ctpt_branchid to the GUID
            } else if (field.name === 'ctpt_branchid') {
              formatted[field.name] = field.id || field.value;
            }
          }
        });
        // Map API field names to display field names
        formatted.discipline = formatted.ctpt_discipline || formatted.discipline || "";
        formatted.highestqualification = formatted.ctpt_qualifications || formatted.highestqualification || "";
        formatted.professionalregistration = formatted.ctpt_professionalregistration || formatted.professionalregistration || "";
        formatted.employmentstatus = getOptionSetLabel(formatted.ctpt_employmentstatus, employmentStatusOptions) || formatted.employmentstatus || "";
        formatted.willingtorelocate = getOptionSetLabel(formatted.ctpt_willingtorelocate, willingToRelocateOptions) || formatted.willingtorelocate || "";
        formatted.employmenttype = getOptionSetLabel(formatted.ctpt_employmenttype, employmentTypeOptions) || formatted.employmenttype || "";
        formatted.applicationtype = getOptionSetLabel(formatted.ctpt_ApplicationType, applicationTypeOptions) || formatted.applicationtype || "";
        // Preserve branch ID fields for province filtering
        formatted.ctpt_branchid = formatted.ctpt_branchid || "";
        formatted._ctpt_branchid_value = formatted._ctpt_branchid_value || "";
        return formatted;
      } else if (record.contactid || record.id) {
        // Format: { contactid: "...", firstname: "...", lastname: "...", ... }
        // For lookup fields, check if they're objects with id/value properties
        let branchId = '';
        let branchIdFormatted = '';
        
        if (record.ctpt_branchid) {
          branchId = typeof record.ctpt_branchid === 'object' 
            ? (record.ctpt_branchid.id || record.ctpt_branchid.value || record.ctpt_branchid || '')
            : record.ctpt_branchid;
        }
        
        if (record._ctpt_branchid_value) {
          branchIdFormatted = typeof record._ctpt_branchid_value === 'object'
            ? (record._ctpt_branchid_value.id || record._ctpt_branchid_value.value || record._ctpt_branchid_value || '')
            : record._ctpt_branchid_value;
        }
        
        // Also check for nested formats like _ctpt_branchid_value@OData.Community.Display.V1.FormattedValue
        const branchIdKeys = Object.keys(record).filter(k => k.includes('branch') || k.includes('Branch'));
        if (branchIdKeys.length > 0 && !branchId && !branchIdFormatted) {
          console.log("Found branch-related keys in record:", branchIdKeys);
          // Try to extract from any branch-related key
          branchIdKeys.forEach(key => {
            const value = record[key];
            if (value && !branchId) {
              branchId = typeof value === 'object' ? (value.id || value.value || '') : value;
            }
          });
        }
        
        return {
          id: record.contactid || record.id,
          contactid: record.contactid || record.id,
          statecode: record.statecode,
          firstname: record.firstname || "",
          lastname: record.lastname || "",
          ctpt_membershipnumber: record.ctpt_membershipnumber || "",
          emailaddress1: record.emailaddress1 || "",
          discipline: record.ctpt_discipline || record.discipline || "",
          highestqualification: record.ctpt_qualifications || record.highestqualification || "",
          professionalregistration: record.ctpt_professionalregistration || record.professionalregistration || "",
          province: record.ctpt_provinces || record.province || "",
          // Preserve branch ID fields for province filtering - use extracted values
          ctpt_branchid: branchId || "",
          _ctpt_branchid_value: branchIdFormatted || branchId || "",
          employmentstatus: getOptionSetLabel(record.ctpt_employmentstatus, employmentStatusOptions) || record.employmentstatus || "",
          willingtorelocate: getOptionSetLabel(record.ctpt_willingtorelocate, willingToRelocateOptions) || record.willingtorelocate || "",
          employmenttype: getOptionSetLabel(record.ctpt_employmenttype, employmentTypeOptions) || record.employmenttype || "",
          applicationtype: getOptionSetLabel(record.ctpt_ApplicationType, applicationTypeOptions) || record.applicationtype || "",
        };
      }
      return null;
    }).filter(Boolean); // Remove null entries
  }, [employmentStatusOptions, willingToRelocateOptions, employmentTypeOptions, applicationTypeOptions]);

  const fetchEmployees = useCallback(() => {
    setLoading(true);
    const api = config.publicAPI.employeeSearch;
    const payload = {
      recordType: "contacts",
      filters: [...CONNECT_SEARCH_FIXED_FILTERS],
      fieldAndOperator: true,
      idfield: "contactid",
      properties: [
        "contactid",
        "firstname",
        "lastname",
        "ctpt_membershipnumber",
        "emailaddress1",
        "ctpt_discipline",
        "ctpt_qualifications",
        "ctpt_professionalregistration",
        "ctpt_provinces",
        "ctpt_branchid",
        "_ctpt_branchid_value",
        "ctpt_employmentstatus",
        "ctpt_willingtorelocate",
        "ctpt_employmenttype",
        "ctpt_applicationtype",
        "ctpt_onhold",
        "ctpt_connectsearch",
      ],
      formattedFields: ["_ctpt_branchid_value"], // Request formatted lookup field to get the GUID
    };

    axios
      .post(api, payload, {
        headers: {
          "Authorization": "Bearer " + localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        console.log("API Response:", res.data); // Debug log
        if (res.data && Array.isArray(res.data)) {
          const filtered = res.data.filter(meetsConnectSearchFixedCriteria);
          const formattedEmployees = formatEmployeeData(filtered);
          console.log("Formatted Employees:", formattedEmployees); // Debug log
          setEmployees(formattedEmployees);
          setSearchData(null); // Clear search data when fetching all
        } else {
          console.log("Unexpected API response format:", res.data);
          setEmployees([]);
          setSearchData(null);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log("Error fetching employees:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to load employee records",
            code: "",
            display: "show",
            severity: "error",
          },
        });
        setLoading(false);
      });
  }, [dispatch, formatEmployeeData]);

  const searchEmployees = useCallback(
    (searchValue, filters = {}) => {
      setIsSearching(true);
      const api = config.publicAPI.employeeSearch;
      const filtersArray = [];
      const textSearchFilters = [];
      const searchVariants = buildSearchVariants(searchValue);

      const applyClientSideTextFilter = (results) => {
        if (!Array.isArray(results)) return [];
        const connectSearchEligibleResults = results.filter(meetsConnectSearchFixedCriteria);
        if (searchVariants.length === 0) return connectSearchEligibleResults;
        return connectSearchEligibleResults.filter((emp) => {
          const fieldsToCheck = [
            emp.firstname,
            emp.lastname,
            emp.discipline,
            emp.highestqualification,
            emp.professionalregistration,
          ];
          return fieldsToCheck.some((field) => matchesAnySearchVariant(field, searchVariants));
        });
      };

      // Add text search filters (First Name, Last Name, Discipline, Highest Qualification, Professional Registration) - these will use OR logic
      if (searchValue && searchValue.trim() !== "") {
        searchVariants.forEach((variant) => {
          textSearchFilters.push(
            {
              field: "firstname",
              searchvalue: variant,
              type: "string",
              operator: "contains",
            },
            {
              field: "lastname",
              searchvalue: variant,
              type: "string",
              operator: "contains",
            },
            {
              field: "ctpt_discipline",
              searchvalue: variant,
              type: "string",
              operator: "contains",
            },
            {
              field: "ctpt_qualifications",
              searchvalue: variant,
              type: "string",
              operator: "contains",
            },
            {
              field: "ctpt_professionalregistration",
              searchvalue: variant,
              type: "string",
              operator: "contains",
            }
          );
        });
      }

      // Add filter field filters - using correct field names, operators, and checking for NOT_SELECTED
      // These will use AND logic with each other (fieldAndOperator: true)
      // Operators as per requirements:
      // - Discipline = contains
      // - Highest Qualifications = contains
      // - Professional Registration = contains
      // - Province = equal to
      // - Employment status = equal to
      // - Willing to relocate = equal to
      // - Employment type = equal to
      // - Application Type = equal to
      if (filters.discipline && filters.discipline !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_discipline",
          searchvalue: filters.discipline,
          type: "string",
          operator: "contains",
        });
      }
      if (filters.highestQualification && filters.highestQualification !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_qualifications",
          searchvalue: filters.highestQualification,
          type: "string",
          operator: "contains",
        });
      }
      if (filters.professionalRegistration && filters.professionalRegistration !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_professionalregistration",
          searchvalue: filters.professionalRegistration,
          type: "string",
          operator: "contains",
        });
      }
      // Province is stored in ctpt_branchid (lookup to branch table), not ctpt_provinces
      // Using hardcoded GUIDs until API provides dynamic values
      // Store province filter separately for client-side filtering due to API lookup field issues
      const provinceToFilter = filters.province && filters.province !== NOT_SELECTED ? filters.province : null;
      if (provinceToFilter) {
        // Find the province option to get its GUID
        const selectedProvince = provinceOptions.find(opt => opt.guid === provinceToFilter || opt.label === provinceToFilter);
        if (selectedProvince) {
          console.log("Province filter will be applied client-side:", selectedProvince);
          // Note: Not adding to filtersArray - will filter results after API call
          // This is a workaround for the Dataverse lookup field filtering issue
        }
      }
      if (filters.employmentStatus && filters.employmentStatus !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_employmentstatus",
          searchvalue: String(filters.employmentStatus), // Ensure string for OptionSet
          type: "optionset",
          operator: "eq",
        });
      }
      if (filters.willingToRelocate && filters.willingToRelocate !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_willingtorelocate",
          searchvalue: String(filters.willingToRelocate), // Ensure string for OptionSet
          type: "optionset",
          operator: "eq",
        });
      }
      if (filters.employmentType && filters.employmentType !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_employmenttype",
          searchvalue: String(filters.employmentType), // Ensure string for OptionSet
          type: "optionset",
          operator: "eq",
        });
      }
      if (filters.applicationType && filters.applicationType !== NOT_SELECTED) {
        filtersArray.push({
          field: "ctpt_applicationtype",
          searchvalue: String(filters.applicationType), // Ensure string for OptionSet
          type: "optionset",
          operator: "eq",
        });
      }

      // Debug: Log what filters we have
      console.log("Filter Debug:", {
        textSearchFilters: textSearchFilters.length,
        filtersArray: filtersArray.length,
        filtersArrayContents: filtersArray,
        provinceToFilter: provinceToFilter,
        allFilters: filters,
        employmentTypeValue: filters.employmentType,
        employmentTypeIsNotSelected: filters.employmentType === NOT_SELECTED,
        willAddEmploymentTypeFilter: filters.employmentType && filters.employmentType !== NOT_SELECTED
      });
      
      // Check if only province is selected (no other filters)
      const hasOnlyProvinceFilter = provinceToFilter && textSearchFilters.length === 0 && filtersArray.length === 0;
      
      // If no filters at all (including province), return empty results
      if (textSearchFilters.length === 0 && filtersArray.length === 0 && !provinceToFilter) {
        console.log("No filters selected - returning empty results");
        setEmployees([]);
        setIsSearching(false);
        return;
      }

      const hasTextSearch = textSearchFilters.length > 0;
      const hasFilterFields = filtersArray.length > 0;
      
      console.log("Search conditions:", {
        hasOnlyProvinceFilter,
        hasTextSearch,
        hasFilterFields
      });
      
      // If only province is selected, we need to fetch all employees and filter client-side
      if (hasOnlyProvinceFilter) {
        // Fetch all employees without any filters, then filter by province client-side
        const payload = {
          recordType: "contacts",
          filters: [...CONNECT_SEARCH_FIXED_FILTERS],
          fieldAndOperator: true,
          idfield: "contactid",
          properties: [...EMPLOYEE_SEARCH_PROPERTIES],
          formattedFields: ["_ctpt_branchid_value"], // Request formatted lookup field to get the GUID
        };

        axios
          .post(api, payload, {
            headers: {
              "Authorization": "Bearer " + localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
          })
        .then((res) => {
            console.log("Province-only search API Response:", res.data);
            if (res.data && Array.isArray(res.data)) {
              const filtered = res.data.filter(meetsConnectSearchFixedCriteria);
              // Debug: Check raw API response structure
              if (filtered.length > 0) {
                console.log("Raw API response sample (first record):", JSON.stringify(filtered[0], null, 2));
              }
              
              const formattedEmployees = formatEmployeeData(filtered);
              console.log("All employees (before province filter):", formattedEmployees.length);
              
              // Apply client-side province filtering
              const selectedProvince = provinceOptions.find(opt => opt.guid === provinceToFilter || opt.label === provinceToFilter);
              if (selectedProvince) {
                console.log("Filtering by province:", selectedProvince.label, "GUID:", selectedProvince.guid);
                // Debug: Check first few employees to see their branch IDs
                if (formattedEmployees.length > 0) {
                  console.log("Sample employee branch data:", {
                    first: formattedEmployees[0].ctpt_branchid,
                    first_formatted: formattedEmployees[0]._ctpt_branchid_value,
                    first_emp: formattedEmployees[0],
                    first_emp_keys: Object.keys(formattedEmployees[0])
                  });
                  
                  // Check multiple employees
                  for (let i = 0; i < Math.min(5, formattedEmployees.length); i++) {
                    console.log(`Employee ${i} branch data:`, {
                      ctpt_branchid: formattedEmployees[i].ctpt_branchid,
                      _ctpt_branchid_value: formattedEmployees[i]._ctpt_branchid_value,
                      all_keys: Object.keys(formattedEmployees[i]).filter(k => k.includes('branch') || k.includes('province'))
                    });
                  }
                }
              
              const finalResults = formattedEmployees.filter(emp => {
                // Check both ctpt_branchid and _ctpt_branchid_value fields
                // Handle different data formats (string, object with id/value, etc.)
                let branchId = '';
                if (emp.ctpt_branchid) {
                  branchId = typeof emp.ctpt_branchid === 'object' ? (emp.ctpt_branchid.id || emp.ctpt_branchid.value || '') : emp.ctpt_branchid;
                } else if (emp._ctpt_branchid_value) {
                  branchId = typeof emp._ctpt_branchid_value === 'object' ? (emp._ctpt_branchid_value.id || emp._ctpt_branchid_value.value || '') : emp._ctpt_branchid_value;
                }
                
                const branchIdStr = String(branchId).toLowerCase().trim();
                const provinceGuid = selectedProvince.guid.toLowerCase().trim();
                const matches = branchIdStr === provinceGuid;
                
                if (matches) {
                  console.log("Province match found:", emp.firstname, emp.lastname, "branchId:", branchId);
                }
                
                return matches;
              });
              console.log("After province filter:", finalResults.length, "results out of", formattedEmployees.length);
              setEmployees(finalResults);
            } else {
              console.warn("Selected province not found in options");
              setEmployees([]);
            }
            setSearchData(null);
          } else {
            console.log("Unexpected API response format:", res.data);
            setEmployees([]);
            setSearchData(null);
          }
          setIsSearching(false);
        })
        .catch((error) => {
          console.log("Error searching employees:", error);
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to search employee records",
              code: "",
              display: "show",
              severity: "error",
            },
          });
          setIsSearching(false);
        });
        return; // Exit early since we handled the province-only case
      }

      // Handle the combination of text search and filter fields
      // The API only supports one fieldAndOperator for all filters.
      // Text search needs OR logic (firstname OR lastname OR membershipnumber)
      // Filter fields need AND logic (discipline AND qualifications AND ...)
      // 
      // When both are present, we need: (text search OR) AND (filter fields AND)
      // Since the API doesn't support grouping, we'll make two API calls and intersect results
      
      if (hasTextSearch && hasFilterFields) {
        // Both text search and filter fields present - make two API calls
        // First: Text search with OR logic
        // Second: Filter fields with AND logic
        // Then: Intersect the results
        // If province is selected, we need to first get branch IDs, then filter by those
        
        // Province filtering is now handled via hardcoded branch GUIDs
        // The province filter is included in filtersArray if selected
        const executeSearchWithProvince = async () => {
          // Use filtersArray which includes province filter if selected
          const localFiltersArray = [...filtersArray];
          
          const textSearchPayload = {
          recordType: "contacts",
          filters: [...CONNECT_SEARCH_FIXED_FILTERS, ...textSearchFilters],
          fieldAndOperator: false, // OR logic for text search (fixed filters must be ANDed by backend with result)
          idfield: "contactid",
          properties: [...EMPLOYEE_SEARCH_PROPERTIES],
          formattedFields: ["_ctpt_branchid_value"], // Request formatted lookup field to get the GUID
        };

        const filterFieldsPayload = {
          recordType: "contacts",
          filters: [...CONNECT_SEARCH_FIXED_FILTERS, ...localFiltersArray], // Fixed criteria + user filter fields
          fieldAndOperator: true, // AND logic for filter fields
          idfield: "contactid",
          properties: [...EMPLOYEE_SEARCH_PROPERTIES],
          formattedFields: ["_ctpt_branchid_value"], // Request formatted lookup field to get the GUID
        };

        console.log("Text Search Payload:", JSON.stringify(textSearchPayload, null, 2));
        console.log("Filter Fields Payload:", JSON.stringify(filterFieldsPayload, null, 2));
        console.log("Active filters being sent to API:", {
          textSearchFilters: textSearchFilters.length,
          filterFields: filterFieldsPayload.filters.length,
          filterDetails: filterFieldsPayload.filters.map(f => ({
            field: f.field,
            operator: f.operator,
            value: f.searchvalue,
            type: f.type
          }))
        });

        // Debug: Test each filter individually to find which one is failing
        if (filtersArray.length > 0) {
          console.log("Testing filters individually...");
          filtersArray.forEach((filter, index) => {
            const singleFilterPayload = {
              recordType: "contacts",
              filters: [filter],
              fieldAndOperator: true,
              idfield: "contactid",
              properties: [
                "contactid",
                "firstname",
                "lastname",
                filter.field,
              ],
            };
            
            axios.post(api, singleFilterPayload, {
              headers: {
                "Authorization": "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            })
            .then((res) => {
              console.log(`Filter ${index + 1} (${filter.field} ${filter.operator} ${filter.searchvalue}):`, res.data?.length || 0, "results");
              if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                console.log(`  First result for ${filter.field}:`, res.data[0]);
              }
            })
            .catch((err) => {
              console.log(`Filter ${index + 1} (${filter.field}) error:`, err);
            });
          });
        }

          // Make both API calls in parallel
          return Promise.all([
            axios.post(api, textSearchPayload, {
              headers: {
                "Authorization": "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            }),
            axios.post(api, filterFieldsPayload, {
              headers: {
                "Authorization": "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            })
          ]);
        };
        
        // Execute the search with province handling
        executeSearchWithProvince().then(([textSearchRes, filterFieldsRes]) => {
          // Check for errors in responses
          if (textSearchRes.data && textSearchRes.data.error) {
            console.error("Text Search API Error:", textSearchRes.data.error);
          }
          if (filterFieldsRes.data && filterFieldsRes.data.error) {
            console.error("Filter Fields API Error:", filterFieldsRes.data.error);
          }
          console.log("Text Search API Response (raw):", JSON.stringify(textSearchRes.data, null, 2));
          console.log("Filter Fields API Response (raw):", JSON.stringify(filterFieldsRes.data, null, 2));
          
          // Format both result sets (apply fixed-criteria filter to raw API data)
          const textSearchResults = textSearchRes.data && Array.isArray(textSearchRes.data) 
            ? applyClientSideTextFilter(
                formatEmployeeData(textSearchRes.data.filter(meetsConnectSearchFixedCriteria))
              )
            : [];
          const filterFieldsResults = filterFieldsRes.data && Array.isArray(filterFieldsRes.data)
            ? applyClientSideTextFilter(
                formatEmployeeData(filterFieldsRes.data.filter(meetsConnectSearchFixedCriteria))
              )
            : [];
          
          console.log("Text Search Results (formatted):", textSearchResults);
          console.log("Filter Fields Results (formatted):", filterFieldsResults);
          
          // Debug: Check first record from filter fields response if it exists
          if (filterFieldsRes.data && Array.isArray(filterFieldsRes.data) && filterFieldsRes.data.length > 0) {
            console.log("First Filter Fields Record (raw):", JSON.stringify(filterFieldsRes.data[0], null, 2));
          }
          
          // Intersect results by contactid - try multiple id field variations
          const textSearchIds = new Set();
          textSearchResults.forEach(emp => {
            if (emp.contactid) textSearchIds.add(String(emp.contactid));
            if (emp.id) textSearchIds.add(String(emp.id));
          });
          
          console.log("Text Search IDs:", Array.from(textSearchIds));
          
          const intersectedResults = filterFieldsResults.filter(emp => {
            const empId = String(emp.contactid || emp.id || '');
            const isMatch = textSearchIds.has(empId);
            if (isMatch) {
              console.log("Match found:", empId, emp);
            }
            return isMatch;
          });
          
          console.log("Intersected Search Results:", intersectedResults);
          console.log("Text Search Count:", textSearchResults.length);
          console.log("Filter Fields Count:", filterFieldsResults.length);
          console.log("Intersected Count:", intersectedResults.length);
          
          // Apply client-side province filtering if province is selected
          let finalResults = intersectedResults;
          if (provinceToFilter) {
            const selectedProvince = provinceOptions.find(opt => opt.guid === provinceToFilter || opt.label === provinceToFilter);
            if (selectedProvince) {
              finalResults = intersectedResults.filter(emp => {
                // Check both ctpt_branchid and _ctpt_branchid_value fields
                // Handle different data formats (string, object with id/value, etc.)
                let branchId = '';
                if (emp.ctpt_branchid) {
                  branchId = typeof emp.ctpt_branchid === 'object' ? (emp.ctpt_branchid.id || emp.ctpt_branchid.value || '') : emp.ctpt_branchid;
                } else if (emp._ctpt_branchid_value) {
                  branchId = typeof emp._ctpt_branchid_value === 'object' ? (emp._ctpt_branchid_value.id || emp._ctpt_branchid_value.value || '') : emp._ctpt_branchid_value;
                }
                
                const branchIdStr = String(branchId).toLowerCase().trim();
                const provinceGuid = selectedProvince.guid.toLowerCase().trim();
                const matches = branchIdStr === provinceGuid;
                
                if (matches) {
                  console.log("Province match found:", emp.firstname, emp.lastname, "branchId:", branchId);
                }
                
                return matches;
              });
              console.log("After province filter:", finalResults.length, "results out of", intersectedResults.length);
            }
          }
          
          setEmployees(finalResults);
          setSearchData(null);
          setIsSearching(false);
        })
        .catch((error) => {
          console.log("Error searching employees:", error);
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to search employee records",
              code: "",
              display: "show",
              severity: "error",
            },
          });
          setIsSearching(false);
        });
      } else {
        // Only text search OR only filter fields - single API call
        const allFilters = hasTextSearch ? textSearchFilters : filtersArray;
        const useAndOperator = hasFilterFields; // true for filter fields, false for text search

        console.log("Single API call - preparing payload:", {
          allFilters,
          allFiltersLength: allFilters.length,
          allFiltersDetails: allFilters.map(f => `${f.field} ${f.operator} ${f.searchvalue} (${f.type})`),
          useAndOperator,
          hasTextSearch,
          hasFilterFields,
          filtersArrayLength: filtersArray.length,
          textSearchFiltersLength: textSearchFilters.length
        });

        const payload = {
          recordType: "contacts",
          filters: [...CONNECT_SEARCH_FIXED_FILTERS, ...allFilters],
          fieldAndOperator: useAndOperator,
          idfield: "contactid",
          properties: [...EMPLOYEE_SEARCH_PROPERTIES],
          formattedFields: ["_ctpt_branchid_value"], // Request formatted lookup field to get the GUID
        };

        console.log("Sending payload to API:", JSON.stringify(payload, null, 2));

        // For text-only searches, backend OR logic can allow text matches that don't meet fixed criteria.
        // Force criteria with a second query and intersect IDs client-side.
        if (hasTextSearch && !hasFilterFields) {
          const textOnlyPayload = {
            recordType: "contacts",
            filters: [...textSearchFilters],
            fieldAndOperator: false,
            idfield: "contactid",
            properties: [...EMPLOYEE_SEARCH_PROPERTIES],
            formattedFields: ["_ctpt_branchid_value"],
          };
          const fixedCriteriaPayload = {
            recordType: "contacts",
            filters: [...CONNECT_SEARCH_FIXED_FILTERS],
            fieldAndOperator: true,
            idfield: "contactid",
            properties: ["contactid"],
          };

          console.log("Text-only payload:", JSON.stringify(textOnlyPayload, null, 2));
          console.log("Fixed-criteria payload:", JSON.stringify(fixedCriteriaPayload, null, 2));

          Promise.all([
            axios.post(api, textOnlyPayload, {
              headers: {
                "Authorization": "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            }),
            axios.post(api, fixedCriteriaPayload, {
              headers: {
                "Authorization": "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            }),
          ])
            .then(([textRes, fixedRes]) => {
              const textRecords = Array.isArray(textRes?.data) ? textRes.data : [];
              const fixedRecords = Array.isArray(fixedRes?.data) ? fixedRes.data : [];
              const extractRecordId = (record) => {
                if (!record) return "";
                if (record.contactid || record.id) return String(record.contactid || record.id);
                if (Array.isArray(record.data)) {
                  const idField = record.data.find((field) => field?.name === "contactid");
                  if (idField?.value) return String(idField.value);
                  if (record.id) return String(record.id);
                }
                return "";
              };

              const fixedIds = new Set(
                fixedRecords
                  .map((record) => extractRecordId(record))
                  .filter(Boolean)
              );

              const strictTextResults = textRecords.filter((record) =>
                fixedIds.has(extractRecordId(record))
              );
              const formattedEmployees = formatEmployeeData(strictTextResults);
              const textMatchedStrictResults = formattedEmployees.filter((emp) => {
                const stateCode = String(emp?.statecode ?? "").toLowerCase().trim();
                const membershipNumber = String(emp?.ctpt_membershipnumber ?? "").trim();
                const isActive = stateCode === "" || stateCode === "0" || stateCode === "active";
                const hasMembershipNumber = membershipNumber !== "";
                if (!isActive || !hasMembershipNumber) return false;
                if (searchVariants.length === 0) return true;
                const fieldsToCheck = [
                  emp.firstname,
                  emp.lastname,
                  emp.discipline,
                  emp.highestqualification,
                  emp.professionalregistration,
                ];
                return fieldsToCheck.some((field) => matchesAnySearchVariant(field, searchVariants));
              });

              console.log("Text-only results count:", textRecords.length);
              console.log("Fixed-criteria results count:", fixedRecords.length);
              console.log("Intersected strict results count:", formattedEmployees.length);
              console.log("Text matched strict results count:", textMatchedStrictResults.length);

              setEmployees(textMatchedStrictResults);
              setSearchData(null);
              setIsSearching(false);
            })
            .catch((error) => {
              console.log("Error searching employees:", error);
              dispatch({
                type: "SET_STATUS",
                payload: {
                  message: "Failed to search employee records",
                  code: "",
                  display: "show",
                  severity: "error",
                },
              });
              setIsSearching(false);
            });
          return;
        }
        
        axios
          .post(api, payload, {
            headers: {
              "Authorization": "Bearer " + localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
          })
        .then((res) => {
          console.log("Search API Response:", res.data);
          if (res.data && Array.isArray(res.data)) {
            const filtered = res.data.filter(meetsConnectSearchFixedCriteria);
            const formattedEmployees = formatEmployeeData(filtered);
            console.log("Formatted Search Results:", formattedEmployees);
            
            // Apply client-side province filtering if province is selected
            let finalResults = formattedEmployees;
            if (provinceToFilter) {
              const selectedProvince = provinceOptions.find(opt => opt.guid === provinceToFilter || opt.label === provinceToFilter);
              if (selectedProvince) {
                finalResults = formattedEmployees.filter(emp => {
                  // Check both ctpt_branchid and _ctpt_branchid_value fields
                  // Handle different data formats (string, object with id/value, etc.)
                  let branchId = '';
                  if (emp.ctpt_branchid) {
                    branchId = typeof emp.ctpt_branchid === 'object' ? (emp.ctpt_branchid.id || emp.ctpt_branchid.value || '') : emp.ctpt_branchid;
                  } else if (emp._ctpt_branchid_value) {
                    branchId = typeof emp._ctpt_branchid_value === 'object' ? (emp._ctpt_branchid_value.id || emp._ctpt_branchid_value.value || '') : emp._ctpt_branchid_value;
                  }
                  
                  const branchIdStr = String(branchId).toLowerCase().trim();
                  const provinceGuid = selectedProvince.guid.toLowerCase().trim();
                  const matches = branchIdStr === provinceGuid;
                  
                  return matches;
                });
                console.log("After province filter:", finalResults.length, "results out of", formattedEmployees.length);
              }
            }
            
            setEmployees(applyClientSideTextFilter(finalResults));
            setSearchData(null);
          } else {
            console.log("Unexpected search API response format:", res.data);
            setEmployees([]);
            setSearchData(null);
          }
          setIsSearching(false);
        })
        .catch((error) => {
          console.log("Error searching employees:", error);
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to search employee records",
              code: "",
              display: "show",
              severity: "error",
            },
          });
          setIsSearching(false);
        });
      }
    },
    [dispatch, formatEmployeeData]
  );

  // Removed initial fetchEmployees() call - search should only happen when user performs a search

  // Helper function to check if at least one filter is selected
  const hasFilterSelected = useCallback(() => {
    return (
      discipline !== NOT_SELECTED ||
      highestQualification !== NOT_SELECTED ||
      professionalRegistration !== NOT_SELECTED ||
      province !== NOT_SELECTED ||
      employmentStatus !== NOT_SELECTED ||
      willingToRelocate !== NOT_SELECTED ||
      employmentType !== NOT_SELECTED ||
      applicationType !== NOT_SELECTED ||
      (searchTerm && searchTerm.trim() !== "")
    );
  }, [
    discipline,
    highestQualification,
    professionalRegistration,
    province,
    employmentStatus,
    willingToRelocate,
    employmentType,
    applicationType,
    searchTerm,
  ]);

  // Handle search with all filters - validate at least one filter is selected
  const handleSearch = useCallback(() => {
    // Check if at least one filter is selected (not NOT_SELECTED)
    if (!hasFilterSelected()) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Please select at least one filter before searching",
          code: "",
          display: "show",
          severity: "warning",
        },
      });
      return;
    }

    const filters = {
      discipline,
      highestQualification,
      professionalRegistration,
      province,
      employmentStatus,
      willingToRelocate,
      employmentType,
      applicationType,
    };
    searchEmployees(searchTerm, filters);
  }, [
    searchTerm,
    discipline,
    highestQualification,
    professionalRegistration,
    province,
    employmentStatus,
    willingToRelocate,
    employmentType,
    applicationType,
    searchEmployees,
    dispatch,
    hasFilterSelected,
  ]);

  // Removed auto-trigger useEffect - search only happens when Search button is clicked

  const handleRequestProfile = (employee) => {
    setSelectedEmployee(employee);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setSelectedEmployee(null);
  };

  const getResponse = (res) => {
    if (res && res.data) {
      // Get employee name from selectedEmployee
      const firstName = selectedEmployee?.firstname || "";
      const lastName = selectedEmployee?.lastname || "";
      const employeeName = `${firstName} ${lastName}`.trim() || "the member";
      
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: `Thank you for using SAICE Connect. Your request to access ${employeeName}'s profile has been successfully submitted. Upon approval, the member's CV will be shared with you.`,
          code: "",
          display: "show",
          severity: "success",
        },
      });
      handleModalClose();
    } else {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Failed to submit profile request. Please try again.",
          code: "",
          display: "show",
          severity: "error",
        },
      });
    }
  };

  const handleCustomSubmit = async (JsonData) => {
    try {
      enableLoader(true, "Please wait, Submitting...");
      
      // Add employeeId to JsonData for the payload function
      const employeeId = selectedEmployee?.contactid || selectedEmployee?.id;
      JsonData.employeeId = employeeId;
      
      // Get the payload in the correct format
      const payload = await CreateEmployeeProfileRequestPayload(JsonData);
      
      // Call the API directly with the full URL
      const apiUrl = config.publicAPI.createProfileRequest;
      const response = await axios.post(apiUrl, payload, {
        headers: {
          // "Authorization": "Bearer " + localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      });
      
      enableLoader(false);
      getResponse(response);
    } catch (error) {
      enableLoader(false);
      console.error("Error submitting profile request:", error);
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: error.response?.data?.message || "Failed to submit profile request. Please try again.",
          code: "",
          display: "show",
          severity: "error",
        },
      });
    }
  };

  const formwidgetOptions = {
    widgetType: "add",
    formSchema: getEmployeeProfileRequestSchema,
    uiSchema: getEmployeeProfileRequestUiSchema,
    apiURL: config.teleportal.submitForm.get,
    entityType: "ctpt_employeeprofilerequests",
    formUischema: "",
    button1Label: "Request Profile",
    button2Label: "Cancel",
    button3Label: "Clear",
    payloadSubmit: async (JsonData) => {
      // Add employeeId to JsonData for the payload function
      const employeeId = selectedEmployee?.contactid || selectedEmployee?.id;
      JsonData.employeeId = employeeId;
      return await CreateEmployeeProfileRequestPayload(JsonData);
    },
    customSubmitCallback: handleCustomSubmit,
    columns: [],
    contactId: selectedEmployee?.contactid || selectedEmployee?.id,
    historyUrl: "",
    hasModel: true,
  };

  const getMembershipNumber = (employee) => {
    return employee?.ctpt_membershipnumber || "N/A";
  };

  const columns = [
    {
      field: "firstname",
      flex: 0.85,
      minWidth: 110,
      headerName: "First Name",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "lastname",
      flex: 0.85,
      minWidth: 110,
      headerName: "Last Name",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "discipline",
      flex: 1.25,
      minWidth: 200,
      headerName: "Discipline(s)",
      headerClassName: clsx("super-app-theme--header", classes.dataGridLongTextHeader),
      cellClassName: classes.dataGridLongTextCell,
      renderCell: renderLongTextCell,
    },
    {
      field: "highestqualification",
      flex: 1.25,
      minWidth: 200,
      headerName: "Qualification(s)",
      headerClassName: clsx("super-app-theme--header", classes.dataGridLongTextHeader),
      cellClassName: classes.dataGridLongTextCell,
      renderCell: renderLongTextCell,
    },
    {
      field: "professionalregistration",
      flex: 1.35,
      minWidth: 220,
      headerName: "Registration(s)",
      headerClassName: clsx("super-app-theme--header", classes.dataGridLongTextHeader),
      cellClassName: classes.dataGridLongTextCell,
      renderCell: renderLongTextCell,
    },
    // {
    //   field: "emailaddress1",
    //   flex: 1.5,
    //   headerName: "Email",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "discipline",
    //   flex: 1,
    //   headerName: "Discipline",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "highestqualification",
    //   flex: 1,
    //   headerName: "Highest Qualification",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "professionalregistration",
    //   flex: 1,
    //   headerName: "Professional Registration",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "province",
    //   flex: 1,
    //   headerName: "Province",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "employmentstatus",
    //   flex: 1,
    //   headerName: "Employment Status",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "willingtorelocate",
    //   flex: 1,
    //   headerName: "Willing to Relocate",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "employmenttype",
    //   flex: 1,
    //   headerName: "Employment Type",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "applicationtype",
    //   flex: 1,
    //   headerName: "Application Type",
    //   headerClassName: "super-app-theme--header",
    // },
    {
      field: "action",
      flex: 1,
      headerName: "Action",
      headerClassName: "super-app-theme--header",
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleRequestProfile(params.row)}
        >
          Request Profile
        </Button>
      ),
    },
  ];

  const getRowId = (row) => {
    return row.contactid || row.id || Math.random();
  };

  // Update schema title with employee name or use default
  const getSchemaWithTitle = () => {
    if (selectedEmployee) {
      const firstName = selectedEmployee?.firstname || "";
      const lastName = selectedEmployee?.lastname || "";
      const membershipNumber = selectedEmployee?.ctpt_membershipnumber || "";
      
      // Use name if available, otherwise membership number, otherwise default
      if (firstName || lastName) {
        const fullName = `${firstName} ${lastName}`.trim();
        return {
          ...getEmployeeProfileRequestSchema,
          title: `Request Profile for ${fullName}`,
        };
      } else if (membershipNumber) {
        return {
          ...getEmployeeProfileRequestSchema,
          title: `Request Profile for Member ${membershipNumber}`,
        };
      }
    }
    // Default title if no employee data available
    return {
      ...getEmployeeProfileRequestSchema,
      title: "Request Profile",
    };
  };

  const body = (
    <div className={classes.paper}>
      <IconButton onClick={handleModalClose} className={classes.modalClose}>
        <CloseIcon />
      </IconButton>
      <div className={classes.modalContainer}>
        <FormWidget
          options={{
            ...formwidgetOptions,
            formSchema: getSchemaWithTitle(),
          }}
          successResponse={getResponse}
          openModel={setOpenModal}
          handleModalClose={handleModalClose}
        />
      </div>
    </div>
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setDiscipline(NOT_SELECTED);
    setHighestQualification(NOT_SELECTED);
    setProfessionalRegistration(NOT_SELECTED);
    setProvince(NOT_SELECTED);
    setEmploymentStatus(NOT_SELECTED);
    setWillingToRelocate(NOT_SELECTED);
    setEmploymentType(NOT_SELECTED);
    setApplicationType(NOT_SELECTED);
    setEmployees([]);
  };


  return (
    <Grid container spacing={0} justify="center" className={classes.container}>
      <Grid item xs={12} md={10}>
        <Typography variant="h4" component="h1" className={classes.title}>
          SAICE Connect Employee Search
        </Typography>
        <Typography variant="h2" component="h2" className={classes.description}>
          Do you have a vacancy? Browse candidates who are currently seeking new employment opportunities.
        </Typography>

        {loading ? (
          <Typography>Loading employee records...</Typography>
        ) : (
          <>
            <div className={classes.searchContainer}>
              <TextField
                label="Search by First Name, Last Name, Discipline, Highest Qualification, or Professional Registration"
                variant="outlined"
                className={classes.searchField}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                disabled={isSearching}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={classes.filtersContainer}>
              <Typography variant="h6" gutterBottom>
                Filter Options
              </Typography>
              <Grid container spacing={2} className={classes.filterRow}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Discipline</InputLabel>
                    <Select
                      value={discipline}
                      onChange={(e) => setDiscipline(e.target.value)}
                      label="Discipline"
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {disciplineOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Highest Qualification</InputLabel>
                    <Select
                      value={highestQualification}
                      onChange={(e) => setHighestQualification(e.target.value)}
                      label="Highest Qualification"
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {highestQualificationOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Professional Registration</InputLabel>
                    <Select
                      value={professionalRegistration}
                      onChange={(e) => setProfessionalRegistration(e.target.value)}
                      label="Professional Registration"
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {professionalRegistrationOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>SAICE Branch</InputLabel>
                    <Select
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                      label="SAICE Branch"
                      renderValue={(selected) => {
                        if (selected === NOT_SELECTED) {
                          return <em>Not Selected</em>;
                        }
                        const selectedProvince = provinceOptions.find(opt => opt.guid === selected);
                        return selectedProvince ? selectedProvince.label : selected;
                      }}
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {provinceOptions.map((option) => (
                        <MenuItem key={option.guid} value={option.guid}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Current Employment Status</InputLabel>
                    <Select
                      value={employmentStatus}
                      onChange={(e) => setEmploymentStatus(e.target.value)}
                      label="Current Employment Status"
                      renderValue={(selected) => {
                        if (selected === NOT_SELECTED) {
                          return <em>Not Selected</em>;
                        }
                        const selectedOption = employmentStatusOptions.find(opt => opt.value === selected);
                        return selectedOption ? selectedOption.label : selected;
                      }}
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {employmentStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Willing to Relocate</InputLabel>
                    <Select
                      value={willingToRelocate}
                      onChange={(e) => setWillingToRelocate(e.target.value)}
                      label="Willing to Relocate"
                      renderValue={(selected) => {
                        if (selected === NOT_SELECTED) {
                          return <em>Not Selected</em>;
                        }
                        const selectedOption = willingToRelocateOptions.find(opt => opt.value === selected);
                        return selectedOption ? selectedOption.label : selected;
                      }}
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {willingToRelocateOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      value={employmentType}
                      onChange={(e) => setEmploymentType(e.target.value)}
                      label="Employment Type"
                      renderValue={(selected) => {
                        if (selected === NOT_SELECTED) {
                          return <em>Not Selected</em>;
                        }
                        const selectedOption = employmentTypeOptions.find(opt => opt.value === selected);
                        return selectedOption ? selectedOption.label : selected;
                      }}
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {employmentTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" className={classes.filterField} fullWidth>
                    <InputLabel>Application Type</InputLabel>
                    <Select
                      value={applicationType}
                      onChange={(e) => setApplicationType(e.target.value)}
                      label="Application Type"
                      renderValue={(selected) => {
                        if (selected === NOT_SELECTED) {
                          return <em>Not Selected</em>;
                        }
                        const selectedOption = applicationTypeOptions.find(opt => opt.value === selected);
                        return selectedOption ? selectedOption.label : selected;
                      }}
                    >
                      <MenuItem value={NOT_SELECTED} disabled>
                        <em>Not Selected</em>
                      </MenuItem>
                      {applicationTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearFilters}
                  style={{ marginRight: 8 }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={isSearching || !hasFilterSelected()}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
              </Box>
            </div>

            {/* Display active filters and result count */}
            {employees.length > 0 && (
              <Box mb={2} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                <Typography variant="body2" style={{ marginBottom: "8px", fontWeight: "bold" }}>
                  Search Results: {employees.length} {employees.length === 1 ? "candidate found" : "candidates found"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Active Filters:{" "}
                  {[
                    searchTerm && `Search: "${searchTerm}"`,
                    discipline !== NOT_SELECTED && `Discipline: ${discipline}`,
                    highestQualification !== NOT_SELECTED && `Qualification: ${highestQualification}`,
                    professionalRegistration !== NOT_SELECTED && `Registration: ${professionalRegistration}`,
                    province !== NOT_SELECTED && `Province: ${provinceOptions.find(opt => opt.guid === province)?.label || province}`,
                    employmentStatus !== NOT_SELECTED && `Employment Status: ${employmentStatusOptions.find(opt => opt.value === employmentStatus)?.label || employmentStatus}`,
                    willingToRelocate !== NOT_SELECTED && `Willing to Relocate: ${willingToRelocateOptions.find(opt => opt.value === willingToRelocate)?.label || willingToRelocate}`,
                    employmentType !== NOT_SELECTED && `Employment Type: ${employmentTypeOptions.find(opt => opt.value === employmentType)?.label || employmentType}`,
                    applicationType !== NOT_SELECTED && `Application Type: ${applicationTypeOptions.find(opt => opt.value === applicationType)?.label || applicationType}`,
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                "& .super-app-theme--header": {
                  backgroundColor: globalTheme.palette.lightBg.main,
                  borderBottom: "1px solid",
                  borderColor: globalTheme.palette.midlightBg.main,
                  fontWeight: "900 !important",
                  height: "55px",
                },
              }}
            >
              <ResponsiveGridContainer height={650}>
                <DataGrid
                  rows={searchData || employees}
                  columns={columns}
                  pageSize={pageSize}
                  rowsPerPageOptions={[]}
                  checkboxSelection={false}
                  components={{ 
                    LoadingOverlay: CustomLoadingOverlay,
                    NoRowsOverlay: CustomNoRowsOverlay
                  }}
                  loading={loading || isSearching}
                  disableExtendRowFullWidth={true}
                  getRowId={getRowId}
                  hideFooterPagination={false}
                  autoHeight={false}
                />
              </ResponsiveGridContainer>
            </Box>
          </>
        )}

        <ThemeProvider theme={globalTheme}>
          <Modal
            open={openModal}
            onClose={handleModalClose}
            aria-labelledby="request-profile-modal-title"
            aria-describedby="request-profile-modal-description"
            className={classes.modal}
          >
            {body}
          </Modal>
        </ThemeProvider>
      </Grid>
    </Grid>
  );
}

export default EmployeeSearch;

